import React, { useEffect, useRef, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { useGlobalSearch } from "../context/GlobalSearchContext";
import { SEARCH_CATEGORIES, getTotalResultCount, highlightMatches } from "../utils/globalSearch";

/**
 * HighlightedText Component
 * Renders text with matching portions highlighted
 */
function HighlightedText({ text, query }) {
  const segments = highlightMatches(text, query);
  return (
    <>
      {segments.map((seg, i) =>
        seg.highlight ? (
          <span key={i} className="font-semibold text-blue-600">
            {seg.text}
          </span>
        ) : (
          <span key={i}>{seg.text}</span>
        )
      )}
    </>
  );
}

// Icons for each category
const CategoryIcons = {
  pages: ({ className }) => (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
      />
    </svg>
  ),
  projects: ({ className }) => (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2H5a2 2 0 00-2-2z"
      />
    </svg>
  ),
  contacts: ({ className }) => (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z"
      />
    </svg>
  ),
  tasks: ({ className }) => (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4"
      />
    </svg>
  ),
  receipts: ({ className }) => (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
      />
    </svg>
  ),
};

/**
 * ResultItem Component
 * Renders a single search result item
 */
function ResultItem({ item, type, isSelected, onSelect, onMouseEnter, query }) {
  const getDisplayText = () => {
    switch (type) {
      case "pages":
        return { primary: item.name, secondary: item.path };
      case "projects":
        return {
          primary: item.name,
          secondary: item.displayCode || `PRJ-${item.user_project_number || ""}`,
        };
      case "contacts":
        return {
          primary: item.contact_name || item.name,
          secondary: item.company || item.email || "",
        };
      case "tasks":
        return {
          primary: item.task_name || item.name,
          secondary: item.status || "",
        };
      case "receipts":
        return {
          primary: item.original_name || item.name || "Receipt",
          secondary: item.created_at
            ? new Date(item.created_at).toLocaleDateString()
            : "",
        };
      default:
        return { primary: item.name, secondary: "" };
    }
  };

  const { primary, secondary } = getDisplayText();

  return (
    <div
      onClick={() => onSelect(item, type)}
      onMouseEnter={onMouseEnter}
      className={`px-3 py-2 cursor-pointer transition-colors ${
        isSelected ? "bg-blue-50" : "hover:bg-gray-50"
      }`}
    >
      <div className="flex items-center gap-3">
        <div className="flex-1 min-w-0">
          <p
            className={`text-sm truncate ${
              isSelected ? "text-blue-700 font-medium" : "text-gray-900"
            }`}
          >
            <HighlightedText text={primary} query={query} />
          </p>
          {secondary && (
            <p className="text-xs text-gray-500 truncate">
              <HighlightedText text={secondary} query={query} />
            </p>
          )}
        </div>
        {isSelected && (
          <div className="flex-shrink-0">
            <kbd className="px-1.5 py-0.5 text-xs font-medium text-gray-400 bg-gray-100 border border-gray-200 rounded">
              ↵
            </kbd>
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * CategorySection Component
 * Renders a section for a category with header and items
 */
function CategorySection({
  category,
  items,
  selectedIndex,
  startIndex,
  onSelect,
  onMouseEnter,
  query,
}) {
  const Icon = CategoryIcons[category.key];
  const label = SEARCH_CATEGORIES[category.key.toUpperCase()]?.label || category.key;

  if (!items || items.length === 0) return null;

  return (
    <div className="py-1">
      {/* Section Header */}
      <div className="px-3 py-1.5 flex items-center gap-2">
        <Icon className="w-4 h-4 text-gray-400" />
        <span className="text-xs font-medium text-gray-500 uppercase tracking-wider">
          {label}
        </span>
        <span className="text-xs text-gray-400">({items.length})</span>
      </div>
      
      {/* Section Items */}
      <div>
        {items.map((item, idx) => {
          const globalIndex = startIndex + idx;
          return (
            <ResultItem
              key={item.id || `${category.key}-${idx}`}
              item={item}
              type={category.key}
              isSelected={selectedIndex === globalIndex}
              onSelect={onSelect}
              onMouseEnter={() => onMouseEnter(globalIndex)}
              query={query}
            />
          );
        })}
      </div>
    </div>
  );
}

/**
 * GlobalSearchResults Component
 * Displays grouped search results with keyboard navigation
 */
export default function GlobalSearchResults({ inputRef }) {
  const navigate = useNavigate();
  const { results, query, closeSearch, hasResults, selectedIndex, setSelectedIndex, isMouseHoverEnabled, setIsMouseHoverEnabled } = useGlobalSearch();
  const listRef = useRef(null);
  const lastMousePosition = useRef({ x: 0, y: 0 });

  // Flatten results for keyboard navigation
  const flattenedResults = useMemo(() => {
    if (!results) return [];
    
    const flattened = [];
    const categories = ["pages", "projects", "contacts", "tasks", "receipts"];
    
    categories.forEach((categoryKey) => {
      const items = results[categoryKey] || [];
      items.forEach((item) => {
        flattened.push({ item, type: categoryKey });
      });
    });
    
    return flattened;
  }, [results]);

  // Calculate start indices for each category
  const categoryStartIndices = useMemo(() => {
    if (!results) return {};
    
    const indices = {};
    let currentIndex = 0;
    const categories = ["pages", "projects", "contacts", "tasks", "receipts"];
    
    categories.forEach((categoryKey) => {
      indices[categoryKey] = currentIndex;
      currentIndex += (results[categoryKey] || []).length;
    });
    
    return indices;
  }, [results]);

  // Reset selected index when query changes, but clamp to valid range
  // This ensures the index resets on new searches but stays valid when reopening
  useEffect(() => {
    if (flattenedResults.length === 0) {
      setSelectedIndex(0);
    } else if (selectedIndex >= flattenedResults.length) {
      // Clamp to last valid index if current selection is out of bounds
      setSelectedIndex(flattenedResults.length - 1);
    }
  }, [query, flattenedResults.length, selectedIndex, setSelectedIndex]);

  // Handle keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!hasResults) return;

      switch (e.key) {
        case "ArrowDown":
          e.preventDefault();
          setSelectedIndex((prev) =>
            prev < flattenedResults.length - 1 ? prev + 1 : prev
          );
          break;
        case "ArrowUp":
          e.preventDefault();
          setSelectedIndex((prev) => (prev > 0 ? prev - 1 : 0));
          break;
        case "Enter":
          e.preventDefault();
          if (flattenedResults[selectedIndex]) {
            const { item, type } = flattenedResults[selectedIndex];
            handleSelect(item, type);
          }
          break;
        default:
          break;
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [flattenedResults, selectedIndex, hasResults]);

  // Scroll selected item into view
  useEffect(() => {
    if (listRef.current && selectedIndex >= 0) {
      const items = listRef.current.querySelectorAll("[data-result-item]");
      if (items[selectedIndex]) {
        items[selectedIndex].scrollIntoView({ block: "nearest" });
      }
    }
  }, [selectedIndex]);

  // Handle result selection
  const handleSelect = (item, type) => {
    closeSearch();

    switch (type) {
      case "pages":
        navigate(item.path);
        break;
      case "projects":
        navigate(`/projects/${item.id}`);
        break;
      case "contacts":
        navigate("/contacts");
        break;
      case "tasks":
        navigate("/Tasks");
        break;
      case "receipts":
        navigate(`/receipts/${item.id}`);
        break;
      default:
        break;
    }
  };

  // Handle mouse enter on item (only if mouse hover is enabled)
  const handleMouseEnter = (index) => {
    if (isMouseHoverEnabled) {
      setSelectedIndex(index);
    }
  };

  // Handle mouse move to re-enable hover after intentional mouse movement
  const handleMouseMove = (e) => {
    // Only re-enable if mouse has actually moved (not just dropdown appearing under cursor)
    const dx = Math.abs(e.clientX - lastMousePosition.current.x);
    const dy = Math.abs(e.clientY - lastMousePosition.current.y);
    
    if (dx > 5 || dy > 5) {
      // Mouse has moved significantly, re-enable hover
      if (!isMouseHoverEnabled) {
        setIsMouseHoverEnabled(true);
      }
      lastMousePosition.current = { x: e.clientX, y: e.clientY };
    }
  };

  // Track initial mouse position when dropdown opens
  useEffect(() => {
    const trackInitialPosition = (e) => {
      lastMousePosition.current = { x: e.clientX, y: e.clientY };
    };
    
    // Capture current mouse position
    document.addEventListener("mousemove", trackInitialPosition, { once: true });
    
    return () => {
      document.removeEventListener("mousemove", trackInitialPosition);
    };
  }, []);

  // Don't show if no query
  if (!query.trim()) {
    return (
      <motion.div
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -10 }}
        className="absolute top-full left-0 right-0 mt-2 bg-white rounded-lg border border-gray-200 shadow-lg overflow-hidden z-50"
      >
        <div className="px-4 py-6 text-center">
          <p className="text-sm text-gray-500">
            Start typing to search pages, projects, contacts, tasks, and receipts...
          </p>
          <div className="mt-3 flex items-center justify-center gap-4 text-xs text-gray-400">
            <span className="flex items-center gap-1">
              <kbd className="px-1.5 py-0.5 bg-gray-100 border border-gray-200 rounded">↑</kbd>
              <kbd className="px-1.5 py-0.5 bg-gray-100 border border-gray-200 rounded">↓</kbd>
              to navigate
            </span>
            <span className="flex items-center gap-1">
              <kbd className="px-1.5 py-0.5 bg-gray-100 border border-gray-200 rounded">↵</kbd>
              to select
            </span>
            <span className="flex items-center gap-1">
              <kbd className="px-1.5 py-0.5 bg-gray-100 border border-gray-200 rounded">esc</kbd>
              to close
            </span>
          </div>
        </div>
      </motion.div>
    );
  }

  // Show no results message
  if (!hasResults) {
    return (
      <motion.div
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -10 }}
        className="absolute top-full left-0 right-0 mt-2 bg-white rounded-lg border border-gray-200 shadow-lg overflow-hidden z-50"
      >
        <div className="px-4 py-6 text-center">
          <div className="w-12 h-12 mx-auto mb-3 rounded-full bg-gray-100 flex items-center justify-center">
            <svg
              className="w-6 h-6 text-gray-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
              />
            </svg>
          </div>
          <p className="text-sm text-gray-900 font-medium">No results found</p>
          <p className="text-xs text-gray-500 mt-1">
            No matches for "{query}"
          </p>
        </div>
      </motion.div>
    );
  }

  const totalCount = getTotalResultCount(results);

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -10 }}
        className="absolute top-full left-0 right-0 mt-2 bg-white rounded-lg border border-gray-200 shadow-lg overflow-hidden z-50"
      >
        {/* Results Header */}
        <div className="px-3 py-2 border-b border-gray-100 bg-gray-50">
          <p className="text-xs text-gray-500">
            {totalCount} result{totalCount !== 1 ? "s" : ""} for "{query}"
          </p>
        </div>

        {/* Results List */}
        <div
          ref={listRef}
          onMouseMove={handleMouseMove}
          className="max-h-80 overflow-y-auto divide-y divide-gray-100"
        >
          {/* Pages Section */}
          {results.pages?.length > 0 && (
            <CategorySection
              category={{ key: "pages" }}
              items={results.pages}
              selectedIndex={selectedIndex}
              startIndex={categoryStartIndices.pages}
              onSelect={handleSelect}
              onMouseEnter={handleMouseEnter}
              query={query}
            />
          )}

          {/* Projects Section */}
          {results.projects?.length > 0 && (
            <CategorySection
              category={{ key: "projects" }}
              items={results.projects}
              selectedIndex={selectedIndex}
              startIndex={categoryStartIndices.projects}
              onSelect={handleSelect}
              onMouseEnter={handleMouseEnter}
              query={query}
            />
          )}

          {/* Contacts Section */}
          {results.contacts?.length > 0 && (
            <CategorySection
              category={{ key: "contacts" }}
              items={results.contacts}
              selectedIndex={selectedIndex}
              startIndex={categoryStartIndices.contacts}
              onSelect={handleSelect}
              onMouseEnter={handleMouseEnter}
              query={query}
            />
          )}

          {/* Tasks Section */}
          {results.tasks?.length > 0 && (
            <CategorySection
              category={{ key: "tasks" }}
              items={results.tasks}
              selectedIndex={selectedIndex}
              startIndex={categoryStartIndices.tasks}
              onSelect={handleSelect}
              onMouseEnter={handleMouseEnter}
              query={query}
            />
          )}

          {/* Receipts Section */}
          {results.receipts?.length > 0 && (
            <CategorySection
              category={{ key: "receipts" }}
              items={results.receipts}
              selectedIndex={selectedIndex}
              startIndex={categoryStartIndices.receipts}
              onSelect={handleSelect}
              onMouseEnter={handleMouseEnter}
              query={query}
            />
          )}
        </div>

        {/* Footer with keyboard hints */}
        <div className="px-3 py-2 border-t border-gray-100 bg-gray-50">
          <div className="flex items-center justify-between text-xs text-gray-400">
            <div className="flex items-center gap-3">
              <span className="flex items-center gap-1">
                <kbd className="px-1 py-0.5 bg-white border border-gray-200 rounded text-[10px]">↑</kbd>
                <kbd className="px-1 py-0.5 bg-white border border-gray-200 rounded text-[10px]">↓</kbd>
                navigate
              </span>
              <span className="flex items-center gap-1">
                <kbd className="px-1 py-0.5 bg-white border border-gray-200 rounded text-[10px]">↵</kbd>
                select
              </span>
            </div>
            <span className="flex items-center gap-1">
              <kbd className="px-1 py-0.5 bg-white border border-gray-200 rounded text-[10px]">esc</kbd>
              close
            </span>
          </div>
        </div>
      </motion.div>
    </AnimatePresence>
  );
}
